import { NonPIIOrder } from "@/types/settlement";
import { summarizeOrderPayment } from "@/utils/orderPaymentSummary";

const PAYMENT_AMOUNT_KEYS = [
  "order_price_amount",
  "order_discount_amount",
  "item_discount_amount",
  "additional_payment_amount",
  "payment_amount",
  "actual_payment_amount",
  "total_amount",
  "total_amount_due",
  "coupon_discount_amount",
  "coupon_discount_price",
  "coupon_shipping_fee_amount",
  "shipping_fee_discount_amount",
  "shipping_fee",
  "total_discount_amount",
  "membership_discount_amount",
  "product_discount_amount",
  "cancel_fee_amount",
  "app_discount_amount",
  "set_product_discount_amount",
  "market_other_discount_amount",
  "points_spent_amount",
  "credits_spent_amount",
  "naver_point",
];

function sanitizeAmountObject(value: any): Record<string, any> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const result: Record<string, any> = {};
  PAYMENT_AMOUNT_KEYS.forEach((key) => {
    if (value[key] !== undefined && value[key] !== null) {
      result[key] = value[key];
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
}

function sanitizePaymentComponents(
  value: any
): Array<Record<string, any>> | undefined {
  if (!Array.isArray(value)) return undefined;
  const components: Array<Record<string, any>> = [];
  value.forEach((component: any) => {
    if (!component || typeof component !== "object") return;
    const sanitized = {
      code: component.code ?? component.payment_method,
      name: component.name ?? component.payment_method_name,
      amount: component.amount ?? component.payment_amount,
    };
    if (sanitized.code || sanitized.name) components.push(sanitized);
  });
  return components.length > 0 ? components : undefined;
}

function sanitizeShippingFeeDetail(
  value: any
): Array<Record<string, any>> | undefined {
  if (!Array.isArray(value)) return undefined;

  const details = value
    .filter((detail: any) => detail && typeof detail === "object")
    .map((detail: any) => ({
      refunded_shipping_fee: detail.refunded_shipping_fee,
    }))
    .filter((detail: any) => detail.refunded_shipping_fee !== undefined);

  return details.length > 0 ? details : undefined;
}

function sanitizeClaimList(
  value: any,
  alternative?: any
): Array<Record<string, any>> | undefined {
  const target = value ?? alternative;
  if (!target) return undefined;
  if (Array.isArray(target)) {
    return target.filter((item) => item && typeof item === "object");
  }
  if (typeof target === "object") {
    return [target];
  }
  return undefined;
}

/**
 * Strips any potential personally identifiable information (PII) from order object.
 */
export function sanitizeOrderNonPII(o: any): NonPIIOrder {
  return {
    order_id: String(o?.order_id || ""),
    order_date: String(o?.order_date || ""),
    payment_date: String(o?.payment_date || ""),
    order_place_name: o?.order_place_name,
    order_place_id: o?.order_place_id,
    payment_method: o?.payment_method,
    payment_method_name: o?.payment_method_name,
    currency: o?.currency,
    payment_amount: o?.payment_amount,
    naver_point:
      o?.naver_point !== undefined && o?.naver_point !== null
        ? o.naver_point
        : o?.actual_order_amount?.naver_point ??
          o?.initial_order_amount?.naver_point,
    initial_order_amount: sanitizeAmountObject(o?.initial_order_amount),
    actual_order_amount: sanitizeAmountObject(o?.actual_order_amount),
    shipping_fee_detail: sanitizeShippingFeeDetail(o?.shipping_fee_detail),
    payment_summary: summarizeOrderPayment(o),
    payment_components: sanitizePaymentComponents(
      o?.payment_components ||
        o?.payment_breakdown ||
        o?.payment_details ||
        o?.actual_order_amount?.payment_components ||
        o?.actual_order_amount?.payment_breakdown ||
        o?.actual_order_amount?.payment_details
    ),
    items: Array.isArray(o?.items) ? o.items : [],
    cancellation: sanitizeClaimList(o?.cancellation, o?.cancellations),
    exchange: sanitizeClaimList(o?.exchange, o?.exchanges),
    return: sanitizeClaimList(o?.return, o?.returns),
  };
}
